import { useCallback, useEffect, useMemo, useState } from 'react';

interface PendingAuthViewTransition<TView extends string> {
  fromView: TView;
  toView: TView;
}

const useAuthViewTransition = <TView extends string>(view: TView) => {
  const [pendingTransition, setPendingTransition] = useState<PendingAuthViewTransition<TView> | null>(null);

  // Keep the current view busy only until route state leaves it; guards may redirect somewhere
  // other than the requested target, so any new view should clear the pending transition.
  useEffect(() => {
    if (!pendingTransition || view === pendingTransition.fromView) {
      return;
    }

    setPendingTransition(null);
  }, [pendingTransition, view]);

  const startViewTransition = useCallback(
    (toView: TView) => {
      setPendingTransition({ fromView: view, toView });
    },
    [view]
  );

  return useMemo(() => ({
    isViewTransitionPending: Boolean(pendingTransition && view === pendingTransition.fromView),
    isTransitioningToView: (targetView: TView) =>
      Boolean(pendingTransition && view === pendingTransition.fromView && pendingTransition.toView === targetView),
    startViewTransition,
  }), [pendingTransition, startViewTransition, view]);
};

export default useAuthViewTransition;
